import type { Case, CaseEvent, CaseStatus } from '@core/domain/model';
import type { CasesSort } from './types';

/** Дело вместе с тем, что определяет его место в списке. */
export interface CaseContext {
  item: Case;
  overdue: CaseEvent | null;
  upcoming: CaseEvent | null;
}

/**
 * Порядок дел в списке — порт `CasesSorting` ([D-13](docs/DECISIONS.md)).
 *
 * Считается здесь, а не в SQL, потому что ключ сортировки — ближайшее событие дела,
 * то есть данные из другой таблицы, которые экран и так собирает. Если однажды
 * появится постраничная загрузка, порядок обязан переехать в SQL целиком: она ломает
 * сортировку, выполненную после выборки.
 */

const statusRank = (status: CaseStatus): number =>
  status === 'Active' ? 0 : status === 'Suspended' ? 1 : 2;

const groupRank = (context: CaseContext): number =>
  context.overdue ? 0 : context.upcoming ? 1 : 2;

/**
 * Внутри группы — по времени, раньше значит выше: самое давнее просроченное опаснее,
 * ближайшее заседание важнее дальнего. У дел без событий ключ общий, и порядок решает
 * следующее правило — свежесть.
 */
function timeKey(context: CaseContext): number {
  const event = context.overdue ?? context.upcoming;
  if (!event) return 0;

  return event.deadlineAt ?? event.startAt;
}

/** Сравнение названий по-русски: «Ёлкин» стоит между «Егоров» и «Жуков», а не в конце. */
const byTitleRu = (a: string, b: string) => a.localeCompare(b, 'ru');

const comparators: Record<CasesSort, (a: CaseContext, b: CaseContext) => number> = {
  urgency: (a, b) =>
    statusRank(a.item.status) - statusRank(b.item.status) ||
    groupRank(a) - groupRank(b) ||
    timeKey(a) - timeKey(b) ||
    b.item.updatedAt - a.item.updatedAt ||
    a.item.id.localeCompare(b.item.id),

  recent: (a, b) => b.item.updatedAt - a.item.updatedAt || a.item.id.localeCompare(b.item.id),

  title: (a, b) => byTitleRu(a.item.title, b.item.title) || a.item.id.localeCompare(b.item.id),

  /**
   * Порядок, заданный перетаскиванием. У дел, которых юрист руками не двигал,
   * `sortOrder` нулевой — они идут первыми по свежести, чтобы новое дело
   * не пряталось в конце списка.
   */
  manual: (a, b) =>
    a.item.sortOrder - b.item.sortOrder ||
    b.item.updatedAt - a.item.updatedAt ||
    a.item.id.localeCompare(b.item.id),
};

/**
 * Закреплённые дела идут первыми при **любой** сортировке.
 *
 * Юрист, который третий день в процессе по одному делу, хочет видеть его первым,
 * чем бы ни закончилась арифметика сроков. Сортировка в JavaScript устойчива,
 * поэтому внутри закреплённых и внутри остальных порядок остаётся выбранным.
 */
export function sortCases(items: CaseContext[], sort: CasesSort): CaseContext[] {
  const ordered = [...items].sort(comparators[sort]);

  return ordered.sort((a, b) => Number(b.item.isPinned) - Number(a.item.isPinned));
}
